/**
 * @description This is class has static methods to deal with different situation in the site.
 */
class CommonUtils {

	/**
	 * @description Transfoms a number into a USD currency string.
	 * @example $1,000.00
	 */
	static toUSD(num: number): string {
		if (isNaN(num)) return ''
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD'
		}).format(num)
	}

	/**
	 * @description Validates the email syntax by using regex.
	 */
	static validateEmail(invalue: string): boolean {
		var regex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/
		var valid = regex.test(invalue)
		return valid
	}

	/**
	 * @description Validates Canada's zip code syntax by using regex.
	 */
	static isValidCaZipCode(value: string): boolean {
		var re = /^[ABCEGHJKLMNPRSTVXYabceghjklmnprstvxy]{1}\d{1}[A-Za-z]{1} *\d{1}[A-Za-z]{1}\d{1}$/
		return (re.test(value))
	}

	/**
	 * @description Validates United States's zip code syntax by using regex.
	 */
	static isValidUsZipCode(value: string): boolean {
		var re = /^\d{5}([\-]\d{4})?$/
		return re.test(value)
	}

	/**
	 * @description Creates a cookie in the browser. If `days` are not set, it won't expire.
	 */
	static createCookie(name: string, value: string, days?: number): void {
		var expires = ''
		if (days) {
			var date = new Date()
			date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
			expires = `; expires=${date.toUTCString()}`
		} else expires = ''
		document.cookie = `${name}=${value}${expires}; path=/`
	}

	/**
	 * @description Erases a cookie in the browser.
	 */
	static eraseCookie(name: string): void {
		CommonUtils.createCookie(name, '', -1)
	}

	/**
	 * @description Reads a cookie from the browser.
	 */
	static readCookie(name: string): string {
		var nameEQ = `${name}=`
		var ca = document.cookie.split(';')
		for (let i = 0; i < ca.length; i++) {
			var c = ca[i]
			while (c.charAt(0) === ' ') {
				c = c.substring(1, c.length)
				if (c.indexOf(nameEQ) === 0) {
					return c.substring(nameEQ.length, c.length)
				}
			}
		}
		return ''
	}

	/**
	 * @description Transforms a number / string into a currency string with (or without) a minus sign.
	 * @examples 
	 * - -$1,000.00  
	 * - $1,000.00
	 */
	static formatCurrency(price: number | string): string {
		price = price.toString().replace(/\$|\,/g, '')
		if (isNaN(Number(price))) price = '0'
		var sign = Number(price) == (price = Math.abs(Number(price)))
		price = Math.floor(price * 100 + 0.50000000001)
		var cents: number | string = price % 100
		price = Math.floor(price / 100).toString()
		if (cents < 10) cents = `0${cents}`
		for (var i = 0; i < Math.floor((price.length - (1 + i)) / 3); i++)
			price = `${price.substring(0, price.length - (4 * i + 3))},${price.substring(price.length - (4 * i + 3))}`
		return `${(sign ? '' : '-')}$${price}.${cents}`
	}

	/**
	 * @description Transforms a number / string into a string representing a number.
	 */
	static formatPoints(points: number | string): string {
		points = points.toString().replace(/\$|\,/g, '')
		for (var i = 0; i < Math.floor((points.length - (1 + i)) / 3); i++)
			points = `${points.substring(0, points.length - (4 * i + 3))},${points.substring(points.length - (4 * i + 3))}`
		return points
	}

	/**
	 * @description Used to read XML response from the back-end.
	 */
	static getXMLDataSetTableCount(dsName: string, xmlString: string): number {
		var returnValue = -1
		var datasetStartIndex = xmlString.toLowerCase().indexOf(`<${dsName.toLowerCase()}>`)
		var datasetEndIndex = xmlString.toLowerCase().indexOf(`</${dsName.toLowerCase()}>`)
		var fieldStartIndex = -1
		var fieldEndIndex = -1
		if (datasetStartIndex > -1 && datasetEndIndex > -1) {
			fieldStartIndex = xmlString.toLowerCase().indexOf('<totalTables>'.toLowerCase(), datasetStartIndex)
			if (fieldStartIndex > -1 && fieldStartIndex < datasetEndIndex) {
				fieldStartIndex = `${fieldStartIndex}<totalTables>`.toLowerCase().length
				fieldEndIndex = xmlString.toLowerCase().indexOf('</totalTables>'.toLowerCase(), fieldStartIndex)
				if (fieldEndIndex > -1 && fieldEndIndex < datasetEndIndex) {
					returnValue = parseInt(
						xmlString.substring(fieldStartIndex, fieldEndIndex)
					)
				}
			}
		}
		return returnValue
	}

	/**
	 * @description Used to read XML response from the back-end.
	 */
	static getXMLDataTableRowCount(dsName: string, dtIndex: number, xmlString: string): number {
		var returnValue = -1
		var datasetStartIndex = xmlString.toLowerCase().indexOf(`<${dsName.toLowerCase()}>`)
		var datasetEndIndex = xmlString.toLowerCase().indexOf(`</${dsName.toLowerCase()}>`)
		var fieldStartIndex = -1
		var fieldEndIndex = -1
		if (datasetStartIndex > -1 && datasetEndIndex > -1) {
			//<t0TotalRows>1</t0TotalRows>
			fieldStartIndex = xmlString.toLowerCase().indexOf((`<t${dtIndex}TotalRows>`).toLowerCase(), datasetStartIndex)
			if (fieldStartIndex > -1 && fieldStartIndex < datasetEndIndex) {
				fieldStartIndex = fieldStartIndex + (`<t${dtIndex}TotalRows>`).toLowerCase().length
				fieldEndIndex = xmlString.toLowerCase().indexOf((`</t${dtIndex}TotalRows>`).toLowerCase(), fieldStartIndex)
				if (fieldEndIndex > -1 && fieldEndIndex < datasetEndIndex) {
					returnValue = parseInt(
						xmlString.substring(fieldStartIndex, fieldEndIndex)
					)
				}
			}
		}
		return returnValue
	}

	/**
	 * @description Used to read XML response from the back-end.
	 */
	static getXMLDataFieldValue(dsName: string, dtIndex: number, rowIndex: number, fieldName: string, xmlString: string): string {
		var returnValue = ''
		var datasetStartIndex = xmlString.toLowerCase().indexOf(`<${dsName.toLowerCase()}>`)
		var datasetEndIndex = xmlString.toLowerCase().indexOf(`</${dsName.toLowerCase()}>`)
		var fieldStartIndex = -1
		var fieldEndIndex = -1
		if (datasetStartIndex > -1 && datasetEndIndex > -1) {
			fieldStartIndex = xmlString
				.toLowerCase().indexOf((`<t${dtIndex}r${rowIndex}${fieldName}>`).toLowerCase(), datasetStartIndex)
			if (fieldStartIndex > -1 && fieldStartIndex < datasetEndIndex) {
				fieldStartIndex = fieldStartIndex + (`<t${dtIndex}r${rowIndex}${fieldName}>`).toLowerCase().length
				fieldEndIndex = xmlString.toLowerCase().indexOf((`</t${dtIndex}r${rowIndex}${fieldName}>`).toLowerCase(), fieldStartIndex)
				if (fieldEndIndex > -1 && fieldEndIndex < datasetEndIndex) {
					returnValue = xmlString.substring(fieldStartIndex, fieldEndIndex)
				}
			}
		}
		return returnValue
	}

	/**
	 * @description Used to read XML response from the back-end.
	 */
	static getXMLTagValue(tagName: string, xmlString: string): string {
		let tagValue = ''
		const tagStartIndex = xmlString.toLowerCase().indexOf(`<${tagName.toLowerCase()}>`)
		const tagEndIndex = xmlString.toLowerCase().indexOf(`</${tagName.toLowerCase()}>`)
		if (tagStartIndex > -1 && tagStartIndex < tagEndIndex) {
			tagValue = xmlString.substring(tagStartIndex + tagName.length + 2, tagEndIndex)
		}
		return tagValue
	}

	/**
	 * @description Removes bad numbers from a string. 
	 */
	static removeBadNumbers(inNumber: string): string {
		var tmpNumber = ''
		for (var i = 0; i < inNumber.length; i++) {
			var tmpStr = inNumber.substring(i, i + 1)
			if (tmpStr === '-' || tmpStr === ' ' || isNaN(Number(tmpStr))) {
			} else {
				tmpNumber = tmpNumber + inNumber.substring(i, i + 1)
			}
		}
		return tmpNumber
	}

	/**
	 * @description Sets a cookie in the browser.
	 */
	static setCookie(c_name: string, value: string, exdays?: number): void {
		var exdate = new Date()
		exdate.setDate(exdate.getDate() + exdays)
		var c_value = `${encodeURIComponent(value)} ${(exdays === null ? '' : `; expires=${exdate.toUTCString()}`)};path = /`
		document.cookie = `${c_name}=${c_value}`
	}

	/**
	 * @description Gets a cookie from the browser.
	 */
	static getCookie(c_name: string): string | null {
		var c_value = document.cookie
		var c_start = c_value.indexOf(` ${c_name}=`)
		if (c_start === -1) {
			c_start = c_value.indexOf(`${c_name}=`)
		}
		if (c_start === -1) {
			c_value = null
		} else {
			c_start = c_value.indexOf('=', c_start) + 1
			var c_end = c_value.indexOf(';', c_start)
			if (c_end === -1) {
				c_end = c_value.length
			}
			c_value = decodeURIComponent(c_value.substring(c_start, c_end))
		}
		return c_value
	}

	/**
	 * @description Toggles the loader visibility.
	 */
	static showHideLoader(action: string, productIndex: number): void {
		var loader = document.getElementById(`loader${productIndex}`)
		var atc = document.getElementById(`ATC${productIndex}`)
		if (action === 'show') {
			try {
				loader.style.display = ''
				loader.style.visibility = 'visible'
				atc.style.display = 'none'
				atc.style.visibility = 'hidden'
			} catch (e) { }
		} else if (action === 'hide') {
			try {
				loader.style.display = 'none'
				loader.style.visibility = 'hidden'
				atc.style.display = ''
				atc.style.visibility = 'visible'
			} catch (e) { }
		}
	}

	/**
	 * @description Shows an HTML element based on a given HTML selector with specified display style.
	 * @param sel - The HTML selector of the element to show.
	 * @param elementIndex - Optional index if the selector matches multiple elements (default is 0).
	 * @param display - Optional display style ('block' or 'flex') to apply to the element (default is 'block').
	 */
	static showElement(sel: string, elementIndex: number = 0, display: 'block' | 'flex' = 'block'): void {
		sel = sel.replace('#', '');
		let element: any = document.getElementById(sel);
		if (element) {
			if (element.length === undefined || element.nodeName === 'SELECT') {
				element.style.display = display;
				element.style.visibility = 'visible';
			} else {
				if (element[elementIndex]) {
					element[elementIndex].style.display = display;
					element[elementIndex].style.visibility = 'visible';
				}
			}
		}
	}

	/**
	 * @description Hides an HTML element based on a given HTML selector.
	 */
	static hideElement(sel: string, elementIndex: number = 0): void {
		sel = sel.replace('#', '')
		let element: any = document.getElementById(sel)
		if (element) {
			if (element.length === undefined || element.nodeName === 'SELECT') {
				element.style.display = 'none'
				element.style.visibility = 'hidden'
			} else {
				element[elementIndex].style.display = 'none'
				element[elementIndex].style.visibility = 'hidden'
			}
		}
	}

	/**
	 * @description Gets the response of a XMLHttpRequest.
	 */
	static getAjaxObject(): boolean {
		var returnValue: any = false
		try {
			returnValue = new XMLHttpRequest()
		} catch (trymicrosoft) {
			try {
				returnValue = new ActiveXObject('Msxml2.XMLHTTP')
			} catch (othermicrosoft) {
				try {
					returnValue = new ActiveXObject('Microsoft.XMLHTTP')
				} catch (failed) {
					alert(failed.message)
					returnValue = false
				}
			}
		}
		return returnValue
	}

	/**
	 * @description Sends error to Google Analytics.
	 */
	static trackError(eventCategory: string, eventAction: string, eventLabel: string, eventParams?: any): void {
		dataLayer.push({
			event: 'trackErrorEvent',
			event_category: eventCategory,
			event_action: eventAction,
			event_label: eventLabel,
			event_value: 1
		})
		if (typeof eventParams === 'object') {
			if (eventParams.event === 'placePointsOrderError') {
				dataLayer.push({
					event: eventParams.event,
					detail: {
						email: window.btoa(encodeURIComponent(eventParams.email)),
						bonvoyEmail: window.btoa(encodeURIComponent(eventParams.bonvoyEmail)),
						bonvoyNumber: window.btoa(encodeURIComponent(eventParams.bonvoyNumber)),
						pointsToRedeem: eventParams.pointsToRedeem,
						errorMessage: eventParams.errorMessage
					}
				})
			}
		}
	}

	/**
	 * @description Sends error to API.
	 */
	static logError(errorType: string, errorMsg: string): void {
		try {
			fetch(errorLogAPIURL, {
				method: 'POST',
				mode: 'no-cors',
				headers: {
					'Content-Type': 'text/plain',
				},
				body: JSON.stringify({
					'brand': 'HB',
					'page': window.location.href,
					'errorType': errorType,
					'errorMsg': errorMsg
				})
			})
		} catch (error) {
			console.log(error)
		}
	}

	/**
	 * @description Toggles the purchase type (can be "points" or "cash").
	 */
	static swapPurchaseType(event: any): void {
		event.preventDefault()
		$(this)
			.addClass('opened')
			.attr('tabindex', '-1')
			.trigger('focus')
			.attr('aria-expanded', 'true')
			.removeClass('closed')
			.next('div:hidden')
			.slideDown('fast')
			.siblings('div:visible')
			.slideUp('fast')
			.prev()
			.removeClass('opened')
			.addClass('closed')
			.attr('tabindex', '0')
			.attr('aria-expanded', 'false')
	}

		/**
	 * Parses a string of XML data and returns its text content.
	 *
	 * @param {string} string - The XML string to parse.
	 * @returns {string | null} The text content of the XML string, or null if the XML string has no text content.
	 */
	static parseXMLData(string: string): string | null {
		var parser = new DOMParser()
		var xmlDoc = parser.parseFromString('<root>' + string + '</root>', 'text/xml')
		var decodedContent = xmlDoc.documentElement.textContent
		return decodedContent
	}

	/**
	 * @description Get category values from localStorage for a kit/add-on item that does not belong to a catagory in the DB.
	 */
	static getInvtIDCategory(invtID:string,alternateID:string): {invtID:string,alternateID:string,category:string,categoryID:string}  {
		let invtIDCategory={invtID:'',alternateID:'',category:'',categoryID:''}
		if (localStorage['invtid_category']!==''&&localStorage['invtid_category']!==undefined) {
			const invtIDCategoryArray=JSON.parse(localStorage['invtid_category'])
			const found=invtIDCategoryArray.find((elem:{invtID:string,alternateID:string})=>elem.invtID===invtID&&elem.alternateID===alternateID)
			if (found!==undefined) {invtIDCategory=found}
		}
		return invtIDCategory
	}
}

/** Sometimes the back-end sends code to be executed in the front-end.
 * These functions needs to be defined at the window level to avoid breaking the site.
 */
function createCookie(name: string, value: string, days?: number): void {
	CommonUtils.createCookie(name, value, days)
}
function eraseCookie(name: string): void {
	CommonUtils.createCookie(name, '', -1)
}
function readCookie(name: string): string {
	return CommonUtils.readCookie(name)
}

// The line below is needed to make it available on jest tests environment without using "export".
if (!(window as any).CommonUtils) (window as any).CommonUtils = CommonUtils

// Function to set error message, toggle error class, and manage error icon
function setError(elementId, message, isValid) {
    const inputControl = document.getElementById(elementId)?.parentElement as HTMLElement | null;
    const errorDisplay = inputControl?.querySelector('.error') as HTMLElement | null;
    let iconElement = inputControl?.querySelector('.icon') as HTMLElement | null;

    if (!inputControl || !errorDisplay) return;

    if (!iconElement) {
        iconElement = document.createElement('div');
        iconElement.className = 'icon';
        inputControl.appendChild(iconElement);
    }

    errorDisplay.textContent = isValid ? '' : message;
    inputControl.classList.toggle('error', !isValid);
    iconElement.style.backgroundImage = isValid ? "url('images/misc/check-icon.svg')" : "url('images/misc/error-icon.svg')";
}

const validateCardCodeFunction = (index: number) => {
	return (value: string) => {
		const cardType = (<HTMLInputElement>document.getElementById(`paymentTypeUI_ddlOrderCardType${index}`)).value
		return patterns[(cardType != 'A' && cardType != 'G') ? 'cvv' : 'amxCardId'].test(value)
	}
} 

// Define regular expressions
const patterns: { [key: string]: RegExp } = {
    'email': /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    'name': /^[a-zA-Z]+(-[a-zA-Z]+)?(?: [a-zA-Z]+(-[a-zA-Z]+)?)*$/,
    'address': /^(?=.*\d)[a-zA-Z0-9\s.,#\-ºâêîôûÂÊÎÔÛàèìòùÀÈÌÒÙáéíóúÁÉÍÓÚüÜçÇñÑ]*$/,
    'city': /^[a-zA-Z\sâêîôûÂÊÎÔÛàèìòùÀÈÌÒÙáéíóúÁÉÍÓÚüÜçÇñÑ]*$/,
    'phone': /^\+?[0-9-]{5,15}$/,
		'cvv':/^\d{3}$/,
		'amxCardId':/^\d{4}$/,
		'selectbox': /^(?!.*--).*$/
};

// Function to validate form field
function validateField(inputElement: HTMLInputElement) {
    const elementId = inputElement.id;
    const value = inputElement.value.trim();
    let isValid = true;
    let errorMessage = '';

    if (formElements.hasOwnProperty(elementId)) {
        const elementType = formElements[elementId].type;
		const customFieldValidation = formElements[elementId].fieldValidation

		if(customFieldValidation) {
			isValid = customFieldValidation(value)
		} else {
			if (patterns.hasOwnProperty(elementType)) {
				isValid = patterns[elementType].test(value);
			}
		}

		errorMessage = formElements[elementId].errMessage;
    }
    if (isValid && !value) {
        isValid = false;
    }

    setError(elementId, errorMessage, isValid);
}

// Function to attach event listeners to form elements
function attachEventListeners(elementIds: string[]) {
    elementIds.forEach(elementId => {
        const inputElement = document.getElementById(elementId) as HTMLInputElement | null;
        if (inputElement) {
            inputElement.addEventListener('blur', () => {
                validateField(inputElement);
            });
        }
    });
}

function validateZipCode(zipCode: string, countryCode: string) {
	if (countryCode === 'US') {
		return CommonUtils.isValidUsZipCode(zipCode)
	} else if (countryCode === 'CA') {
		return CommonUtils.isValidCaZipCode(zipCode)
	}
	return true
}

// Define form elements
const formElements = {
	emailAddress: { type: 'email', errMessage: 'You must enter a valid email address.' },
	addressUI_tbBillAddressName: { type: 'name', errMessage: 'You must enter a valid Billing Name.' },
	addressUI_tbBillAddressFirstName: { type: 'name', errMessage: 'You must enter a valid Billing First Name.' },
	addressUI_tbBillAddressLastName: { type: 'name', errMessage: 'You must enter a valid Billing Last Name.' },
	addressUI_tbBillAddr1: { type: 'address', errMessage: 'Include your street number to proceed.' },
	addressUI_tbBillAddr2: { type: 'text' },
	addressUI_tbBillAddrCity: { type: 'city', errMessage: 'You must enter a valid Billing City.' },
	addressUI_tbShipAddressName: { type: 'name', errMessage: 'You must enter a valid Shipping Name.' },
	addressUI_tbShipAddressFirstName: { type: 'name', errMessage: 'You must enter a valid Shipping First Name.' },
	addressUI_tbShipAddressLastName: { type: 'name', errMessage: 'You must enter a valid Shipping Last Name.' },
	addressUI_tbShipAddr1: { type: 'address', errMessage: 'Include your street number to proceed.' },
	addressUI_tbShipAddr2: { type: 'text' },
	addressUI_tbShipAddrCity: { type: 'city', errMessage: 'You must enter a valid Shipping City.' },
	addressUI_tbBillAddrZip: { 
		type: 'zip', 
		errMessage: 'You must enter a valid Billing Postal Code.' ,
		fieldValidation: () => {
			const shippingCountryCode = (<HTMLSelectElement>document.getElementById('addressUI_ddlShipAddrCountry')).value
			const zipCode = (<HTMLInputElement>document.getElementById('addressUI_tbShipAddrZip')).value
			return validateZipCode(zipCode, shippingCountryCode)
		} 
	},
	addressUI_tbShipAddrZip: { 
		type: 'zip', 
		errMessage: 'You must enter a valid Shipping Postal Code.', 
		fieldValidation: () => {
			const shippingCountryCode = (<HTMLSelectElement>document.getElementById('addressUI_ddlShipAddrCountry')).value
			const zipCode = (<HTMLInputElement>document.getElementById('addressUI_tbShipAddrZip')).value
			return validateZipCode(zipCode, shippingCountryCode)
		} 
	},
	addressUI_tbBillAddrPhone: { type: 'phone', errMessage: 'You must enter a valid Billing Phone Number.' },
	addressUI_tbShipAddrPhone: { type: 'phone', errMessage: 'You must enter a valid Shipping Phone Number.' },
	addressUI_ddlBillAddrCountry: { type: 'selectbox', errMessage: '' },
	addressUI_ddlBillAddrState: { type: 'selectbox', errMessage: 'You must enter a valid Billing State/Province.', hiddenInputIDs: ['addressUI_hBillAddrState', 'hBillState'] },
	addressUI_ddlShipAddrCountry: { type: 'selectbox', errMessage: '' },
	addressUI_ddlShipAddrState: { type: 'selectbox', errMessage: 'You must enter a valid Shipping State/Province.', hiddenInputIDs: ['addressUI_hShipAddrState', 'hShipState'] },
	addressUI_cSameAsShipping: { type: 'checkbox', errMessage: '' },
	FirstName: { type: 'name', errMessage: 'You must enter a valid First Name.' },
	LastName: { type: 'name', errMessage: 'You must enter a valid Last Name.' },
	contactEmailAddress: { type: 'email', errMessage: 'You must enter a valid email address.' },
	Phone: { type: 'phone', errMessage: 'You must enter a valid Phone Number.' },
	paymentTypeUI_tbCreditCardName: { type: 'name', errMessage: 'You must a valid Name on card.' },
	paymentTypeUI_tbCreditCardCode: { 
		type: 'cvv', 
		errMessage: 'You must enter a valid CVV.', 
		fieldValidation: validateCardCodeFunction(1) 
	},
	paymentTypeUI_tbCreditCardCode2: { 
		type: 'cvv', 
		errMessage: 'You must enter a valid CVV.', 
		fieldValidation: validateCardCodeFunction(2) 
	}
};

// Attach event listeners after DOM content is loaded
document.addEventListener('DOMContentLoaded', () => {
    // Assuming you have an array of element IDs to attach the event listener to
    const elementIds: string[] = [
			'addressUI_tbBillAddressFirstName', 
			'addressUI_tbBillAddressLastName', 
			'addressUI_tbBillAddressName', 
			'addressUI_tbBillAddressName', 
			'addressUI_tbBillAddrPhone', 
			'addressUI_tbBillAddr1', 
			'addressUI_tbBillAddrCity', 
			'addressUI_ddlBillAddrState', 
			'addressUI_tbBillAddrState', 
			'addressUI_tbBillAddrZip', 
			'addressUI_tbShipAddressFirstName', 
			'addressUI_tbShipAddressLastName', 
			'addressUI_tbShipAddressName', 
			'addressUI_tbShipAddrPhone', 
			'addressUI_tbShipAddr1', 
			'addressUI_ddlShipAddrCountry', 
			'addressUI_tbShipAddrCity', 
			'addressUI_ddlShipAddrState', 
			'addressUI_tbShipAddrState', 
			'addressUI_tbShipAddrZip', 
			'emailAddress',
			'FirstName',
			'LastName',
			'Phone',
			'paymentTypeUI_tbCreditCardName',
			'paymentTypeUI_tbCreditCardCode',
			'paymentTypeUI_tbCreditCardCode2',
			'contactEmailAddress',			
		];

    // Attach the event listener to each element
    attachEventListeners(elementIds);
});



document.addEventListener('DOMContentLoaded', function() {
    const increaseBtns: NodeListOf<HTMLButtonElement> = document.querySelectorAll('.increase-btn');
    const decreaseBtns: NodeListOf<HTMLButtonElement> = document.querySelectorAll('.decrease-btn');

	increaseBtns.forEach(function(increaseBtn) {
		increaseBtn.addEventListener('click', function() {
			const quantityInput = this.parentNode?.querySelector('input[name="quantity"]');
			const decreaseBtn = this.parentNode?.querySelector('.decrease-btn');
	
			if (quantityInput instanceof HTMLInputElement && decreaseBtn instanceof HTMLButtonElement) {
				let currentValue = parseInt(quantityInput.value);
				currentValue = isNaN(currentValue) ? 1 : currentValue;
				currentValue = Math.min(currentValue + 1, parseInt(quantityInput.max));
				quantityInput.value = currentValue.toString();
	
				if (currentValue > parseInt(quantityInput.min)) {
					decreaseBtn.removeAttribute('disabled');
					decreaseBtn.classList.remove('disabled');
				}
	
				if (currentValue >= 99) {
					increaseBtn.disabled = true;
					increaseBtn.classList.add('disabled');
				} else {
					increaseBtn.disabled = false;
					increaseBtn.classList.remove('disabled');
				}
			}
		});
	});
	
	decreaseBtns.forEach(function(decreaseBtn) {
		decreaseBtn.addEventListener('click', function() {
			const quantityInput = this.parentNode?.querySelector('input[name="quantity"]');
			const increaseBtn = this.parentNode?.querySelector('.increase-btn');
	
			if (quantityInput instanceof HTMLInputElement && increaseBtn instanceof HTMLButtonElement) {
				let currentValue = parseInt(quantityInput.value);
				currentValue = isNaN(currentValue) ? 1 : currentValue;
				currentValue = Math.max(currentValue - 1, parseInt(quantityInput.min));
				quantityInput.value = currentValue.toString();
	
				if (currentValue < parseInt(quantityInput.max)) {
					increaseBtn.removeAttribute('disabled');
					increaseBtn.classList.remove('disabled');
				}
	
				if (currentValue <= 1) {
					decreaseBtn.disabled = true;
					decreaseBtn.classList.add('disabled');
				} else {
					decreaseBtn.disabled = false;
					decreaseBtn.classList.remove('disabled');
				}
			}
		});
	});
	

	
	const inputs = document.querySelectorAll('.qty-input');
	inputs.forEach((input) => {
		input.addEventListener('input', (event) => {
			const inputElement = event.target as HTMLInputElement;
			let value = inputElement.value;
			// Remove leading zeros and ensure the input is a number from 1 to 99
			value = value.replace(/^0+/, ''); // Remove leading zeros
			const regex = /^[1-9][0-9]?$/; // Match numbers from 1 to 99
			if (!regex.test(value)) {
				// If not, adjust the value to the last valid input
				value = value.replace(/[^0-9]/g, ''); // Remove non-numeric characters
				if (value.length > 2) {
					value = value.slice(0, 2); // Keep only the first two digits
				}
				inputElement.value = value;
			}
		});
	});

});